import React, { useEffect, useMemo, useState } from "react";
import { 
  DataGrid,
} from "@mui/x-data-grid";
import { useTranslation } from "react-i18next";
import { tokens } from "../../theme";
import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import LockOpenOutlinedIcon from "@mui/icons-material/LockOpenOutlined";
import SecurityOutlinedIcon from "@mui/icons-material/SecurityOutlined";
import { Box, Card, MenuItem, TextField, Typography, useTheme, Chip, Avatar, IconButton, Tooltip } from "@mui/material";
import Headeer from "../../components/Headeer";
import GradientHeaderIconButton from "../../components/GradientHeaderIconButton";
import { useNavigate } from "react-router-dom";
import EmailIcon from "@mui/icons-material/Email";
import BarChartIcon from "@mui/icons-material/BarChart";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DoctorService from "../../services/doctorService";
import CollapsibleListFilters, {
  ListFiltersExcelButton,
  listFiltersSelectProps,
} from "../../components/CollapsibleListFilters";

export default function Team() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { t } = useTranslation('team');
  const doctorService = useMemo(() => new DoctorService(), []);
  const navigate = useNavigate();
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [currentDoctor, setCurrentDoctor] = useState(null);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 5,
  });
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filters, setFilters] = useState({
    doctorName: "",
    email: "",
    role: "",
    specialization: "",
    dateFrom: "",
    dateTo: "",
  });
  const [appliedFilters, setAppliedFilters] = useState({});
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    const fetchAllDoctor = async () => {
      try {
        setLoading(true);
        const response = await doctorService.getAllDoctor({
          withMeta: true,
          params: appliedFilters,
          headers: {
            "x-bone-pageindex": paginationModel.page,
            "x-bone-limit": paginationModel.pageSize,
            "x-bone-sortby": "createdAt",
            "x-bone-sortorder": "DESC",
          },
        });
        setDoctors(Array.isArray(response?.data) ? response.data : []);
        setTotalCount(Number(response?.headers?.["x-bone-totalcount"]) || 0);
      } catch (error) {
        console.error("Error fetching doctor info:", error);
        setDoctors([]);
        setTotalCount(0);
      } finally {
        setLoading(false);
      }
    };
    fetchAllDoctor();
  }, [doctorService, appliedFilters, paginationModel.page, paginationModel.pageSize]);

  // Mevcut doktor bilgisini çek ve hiyerarşi kökü olarak kullan
  useEffect(() => {
    const fetchCurrent = async () => {
      try {
        const info = await doctorService.getDoctorInfo();
        // API bu yapıda dönebiliyor: { doctor: { ... } }
        const doctorData = info?.doctor || info || null;
        setCurrentDoctor(doctorData);
      } catch (e) {
        console.error("Current doctor info error:", e);
      }
    };
    fetchCurrent();
  }, [doctorService]);

  const hierarchyTree = useMemo(() => {
    if (!currentDoctor?.id || doctors.length === 0) return [];

    const nodeMap = new Map();
    
    // Add current doctor to map (ensure it's in the map even if not in doctors list)
    nodeMap.set(String(currentDoctor.id), { ...currentDoctor, children: [] });
    
    // Add all doctors to map (convert IDs to strings for consistency)
    doctors.forEach((doc) => {
      const docId = String(doc.id);
      const isCurrentDoctor = docId === String(currentDoctor.id);
      
      if (!nodeMap.has(docId)) {
        // If this is the current doctor, use currentDoctor data to preserve correct role
        if (isCurrentDoctor) {
          nodeMap.set(docId, { ...currentDoctor, children: [] });
        } else {
          nodeMap.set(docId, { ...doc, children: [] });
        }
      } else {
        // Merge but prioritize currentDoctor data for the current doctor to preserve correct role
        const existing = nodeMap.get(docId);
        if (isCurrentDoctor) {
          nodeMap.set(docId, { ...currentDoctor, children: existing.children || [] });
        } else {
          nodeMap.set(docId, { ...existing, ...doc, children: existing.children || [] });
        }
      }
    });

    // If current doctor is a regular doctor (not admin), show parent admin and siblings
    if (currentDoctor?.role === 'doctor' && currentDoctor?.created_by) {
      const parentAdminId = String(currentDoctor.created_by);
      
      // Find parent admin in doctors list
      const parentAdmin = doctors.find(doc => String(doc.id) === parentAdminId);
      
      if (parentAdmin) {
        // Add parent admin to map if not already there
        if (!nodeMap.has(parentAdminId)) {
          nodeMap.set(parentAdminId, { ...parentAdmin, children: [] });
        }
        
        // Find all doctors created by the same admin (siblings + current doctor)
        const siblings = doctors.filter(doc => 
          String(doc.created_by) === parentAdminId
        );
        
        // Add siblings to map and as children of parent admin
        siblings.forEach(sibling => {
          const siblingId = String(sibling.id);
          if (!nodeMap.has(siblingId)) {
            nodeMap.set(siblingId, { ...sibling, children: [] });
          }
          
          const parent = nodeMap.get(parentAdminId);
          if (parent && !parent.children.some(child => String(child.id) === siblingId)) {
            parent.children.push(nodeMap.get(siblingId));
          }
        });
        
        // Return parent admin as root
        return [nodeMap.get(parentAdminId)];
      }
    }

    // For admin doctors, build normal parent-child relationships
    nodeMap.forEach((node) => {
      if (node.created_by) {
        // Convert created_by to string for comparison
        const parentId = String(node.created_by);
        const nodeId = String(node.id);
        
        // Check if parent exists and it's not the same node
        if (parentId !== nodeId && nodeMap.has(parentId)) {
          const parent = nodeMap.get(parentId);
          if (parent && !parent.children.some(child => String(child.id) === nodeId)) {
            parent.children.push(node);
          }
        }
      }
    });

    // Return root node (current doctor) with children
    const rootId = String(currentDoctor.id);
    const root = nodeMap.get(rootId);
    return root ? [root] : [];
  }, [doctors, currentDoctor]);

  const renderNode = (node, depth = 0) => {
    const initials = `${node.name?.[0] || ""}${node.surname?.[0] || ""}`.toUpperCase();
    const isRoot = depth === 0;
    const isCurrentDoctor = String(node.id) === String(currentDoctor?.id);

    return (
      <Box key={node.id || `${node.name}-${depth}`} sx={{ ml: depth === 0 ? 0 : 2, mt: depth === 0 ? 0 : 1 }}>
        <Box
          sx={{
            p: 1.5,
            borderRadius: 2,
            background: isRoot
              ? `linear-gradient(135deg, ${colors.blueAccent[700]} 0%, ${colors.greenAccent[600]} 100%)`
              : isCurrentDoctor
              ? `linear-gradient(135deg, ${colors.greenAccent[700]} 0%, ${colors.blueAccent[600]} 100%)`
              : colors.primary[500],
            border: isCurrentDoctor 
              ? `2px solid ${colors.greenAccent[400]}`
              : `1px solid ${colors.grey[800]}40`,
            boxShadow: isCurrentDoctor
              ? `0 6px 18px ${colors.greenAccent[500]}50`
              : `0 6px 18px ${colors.primary[900]}30`,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            position: "relative",
          }}
        >
          <Avatar
            sx={{
              width: 40,
              height: 40,
              fontWeight: 700,
              background: isRoot
                ? colors.primary[500]
                : isCurrentDoctor
                ? `linear-gradient(135deg, ${colors.greenAccent[600]} 0%, ${colors.greenAccent[700]} 100%)`
                : `linear-gradient(135deg, ${colors.blueAccent[500]} 0%, ${colors.greenAccent[500]} 100%)`,
              color: colors.grey[100],
              border: isCurrentDoctor
                ? `2px solid ${colors.greenAccent[400]}`
                : `1px solid ${colors.grey[700]}40`,
            }}
          >
            {initials || "DR"}
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontWeight: 700, color: colors.grey[100] }}>
              {node.name} {node.surname}
            </Typography>
            <Typography variant="caption" sx={{ color: colors.grey[300], display: "block" }}>
              {node.specialization || t('doctorDefault')} · {node.role || "doctor"}
            </Typography>
          </Box>
          {isRoot && currentDoctor?.role === 'admin' && (
            <Chip
              label={t('you')}
              size="small"
              sx={{
                backgroundColor: colors.primary[500],
                color: colors.grey[100],
                fontWeight: 700,
                border: `1px solid ${colors.grey[700]}60`,
              }}
            />
          )}
          {isCurrentDoctor && !isRoot && (
            <Chip
              label={t('you')}
              size="small"
              sx={{
                backgroundColor: colors.greenAccent[600],
                color: colors.grey[100],
                fontWeight: 700,
                border: `1px solid ${colors.greenAccent[500]}60`,
              }}
            />
          )}
          {isRoot && currentDoctor?.role === 'doctor' && (
            <Chip
              label={t('adminDoctor')}
              size="small"
              sx={{
                backgroundColor: colors.primary[500],
                color: colors.grey[100],
                fontWeight: 700,
                border: `1px solid ${colors.grey[700]}60`,
              }}
            />
          )}
        </Box>

        {node.children && node.children.length > 0 && (
          <Box
            sx={{
              ml: 2.5,
              mt: 1,
              pl: 2,
              borderLeft: `2px dashed ${colors.blueAccent[700]}`,
            }}
          >
            {node.children.map((child) => renderNode(child, depth + 1))}
          </Box>
        )}
      </Box>
    );
  };

  const clickToInviteDoctor = () => {
    navigate("/inviteDoctor");
  };

  const goTeamStatistics = () => {
    navigate("/team/statistics");
  };

  const buildCleanFilters = (source) => {
    const next = {};
    Object.entries(source).forEach(([key, value]) => {
      if (value !== undefined && value !== null && String(value).trim() !== "") {
        next[key] = String(value).trim().toLowerCase();
      }
    });
    return next;
  };

  const activeFilterCount = Object.keys(appliedFilters).length;
  const handleExcelExport = async () => {
    try {
      setExporting(true);
      const response = await doctorService.exportDoctorsExcel(appliedFilters);
      const contentDisposition = response.headers?.["content-disposition"] || "";
      const match = contentDisposition.match(/filename="?([^"]+)"?/i);
      const fileName = match?.[1] || `doctors-${Date.now()}.xlsx`;
      const blob = new Blob([response.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Doctor excel export failed:", error);
    } finally {
      setExporting(false);
    }
  };
  const columns = [
    {
      field: 'doctor',
      headerName: t('doctor'),
      flex: 1.5,
      minWidth: 220,
      /* Quick filter getRowFormattedValue → valueGetter; v6 tek argüman: { row, value, field, ... } */
      valueGetter: (params) =>
        [params.row?.name, params.row?.surname, params.row?.specialization]
          .filter(Boolean)
          .join(" ")
          .trim(),
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar
            sx={{
              width: 36,
              height: 36,
              background: `linear-gradient(135deg, ${colors.blueAccent[500]} 0%, ${colors.greenAccent[500]} 100%)`,
              fontWeight: 700,
              fontSize: '0.85rem'
            }}
          >
            {params.row.name?.charAt(0)}{params.row.surname?.charAt(0)}
          </Avatar>
          <Box>
            <Box sx={{ fontWeight: 600, color: colors.grey[100] }}>
              {params.row.name} {params.row.surname}
            </Box>
            <Box sx={{ fontSize: '0.75rem', color: colors.grey[400] }}>
              {params.row.specialization || t('doctorDefault')}
            </Box>
          </Box>
        </Box>
      ),
    },
    {
      field: 'email',
      headerName: t('email'),
      flex: 1.2,
      minWidth: 200,
    },
    {
      field: 'phone_number',
      headerName: t('phoneNumber'),
      flex: 1,
      minWidth: 150,
    },
    {
      field: 'role',
      headerName: t('accessLevel'),
      flex: 1,
      minWidth: 150,
      renderCell: (params) => {
        const isAdmin = params.value === 'admin';
        const isUser = params.value === 'user';
        
        return (
          <Chip
            icon={
              isAdmin ? <AdminPanelSettingsOutlinedIcon /> :
              isUser ? <LockOpenOutlinedIcon /> :
              <SecurityOutlinedIcon />
            }
            label={params.value?.toUpperCase() || 'USER'}
            size="small"
            sx={{
              backgroundColor: isAdmin 
                ? `${colors.greenAccent[700]}40` 
                : isUser 
                ? `${colors.blueAccent[700]}40`
                : `${colors.grey[700]}40`,
              color: isAdmin 
                ? colors.greenAccent[300] 
                : isUser 
                ? colors.blueAccent[300]
                : colors.grey[300],
              fontWeight: 700,
              border: `1px solid ${
                isAdmin 
                  ? colors.greenAccent[500] 
                  : isUser 
                  ? colors.blueAccent[500]
                  : colors.grey[500]
              }40`,
            }}
          />
        );
      }
    },
    {
      field: 'actions',
      headerName: t('actions'),
      flex: 0.6,
      minWidth: 100,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Tooltip title={t('viewDetails')}>
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/doctors/${params.id}`);
            }}
            sx={{
              backgroundColor: `${colors.blueAccent[700]}40`,
              border: `1px solid ${colors.blueAccent[500]}40`,
              '&:hover': {
                backgroundColor: `${colors.blueAccent[600]}60`,
                transform: 'scale(1.1)',
              },
              transition: 'all 0.3s ease',
            }}
          >
            <VisibilityIcon sx={{ fontSize: 18, color: colors.blueAccent[300] }} />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 2,
          mb: 3,
        }}
      >
        <Headeer title={t('title')} subtitle={t('subtitle')} />

        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "center" }}>
          <GradientHeaderIconButton
            onClick={goTeamStatistics}
            variant="statistics"
            aria-label={t("teamStatistics")}
            icon={<BarChartIcon sx={{ mr: 1 }} />}
          >
            {t("teamStatistics")}
          </GradientHeaderIconButton>

          <GradientHeaderIconButton
            onClick={clickToInviteDoctor}
            variant="primary"
            aria-label={t("inviteDoctor")}
            icon={<EmailIcon sx={{ mr: 1 }} />}
          >
            {t("inviteDoctor")}
          </GradientHeaderIconButton>
        </Box>
      </Box>

      {/* Hierarchy overview */}
      <Card
        sx={{
          mb: 3,
          p: 2.5,
          borderRadius: "16px",
          background: `linear-gradient(135deg, ${colors.primary[400]} 0%, ${colors.primary[500]} 100%)`,
          border: `1px solid ${colors.grey[800]}30`,
          boxShadow: `0 8px 24px rgba(0, 0, 0, 0.18)`,
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 800, color: colors.grey[100] }}>
              {t('teamHierarchy')}
            </Typography>
            <Typography variant="body2" sx={{ color: colors.grey[300] }}>
              {currentDoctor?.role === 'admin' 
                ? t('hierarchyDescriptionAdmin')
                : t('hierarchyDescriptionDoctor')}
            </Typography>
          </Box>
          <Chip
            label={t('hierarchicalView')}
            sx={{
              backgroundColor: `${colors.blueAccent[700]}40`,
              color: colors.blueAccent[100],
              fontWeight: 700,
              border: `1px solid ${colors.blueAccent[500]}40`,
            }}
          />
        </Box>

        <Box sx={{ maxHeight: 360, overflowY: "auto", pr: 1 }}>
          {hierarchyTree.length === 0 ? (
            <Typography variant="body2" sx={{ color: colors.grey[300], fontStyle: "italic" }}>
              {t('hierarchyError')}
            </Typography>
          ) : (
            hierarchyTree.map((node) => renderNode(node))
          )}
        </Box>
      </Card>

      <CollapsibleListFilters
        colors={colors}
        open={filtersOpen}
        onToggle={() => setFiltersOpen((prev) => !prev)}
        activeFilterCount={activeFilterCount}
        onApply={() => {
          setPaginationModel((prev) => ({ ...prev, page: 0 }));
          setAppliedFilters(buildCleanFilters(filters));
        }}
        onClear={() => {
          setFilters({ doctorName: "", email: "", role: "", specialization: "", dateFrom: "", dateTo: "" });
          setPaginationModel((prev) => ({ ...prev, page: 0 }));
          setAppliedFilters({});
        }}
        exportAction={
          <ListFiltersExcelButton
            colors={colors}
            onClick={handleExcelExport}
            disabled={exporting}
            loading={exporting}
          />
        }
      >
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(12, 1fr)", gap: 2 }}>
          <Box sx={{ gridColumn: { xs: "span 12", md: "span 3" } }}>
            <TextField
              fullWidth
              size="small"
              label="Doctor Name"
              value={filters.doctorName}
              onChange={(e) => setFilters((prev) => ({ ...prev, doctorName: e.target.value }))}
            />
          </Box>
          <Box sx={{ gridColumn: { xs: "span 12", md: "span 3" } }}>
            <TextField
              fullWidth
              size="small"
              label="Email"
              value={filters.email}
              onChange={(e) => setFilters((prev) => ({ ...prev, email: e.target.value }))}
            />
          </Box>
          <Box sx={{ gridColumn: { xs: "span 12", md: "span 3" } }}>
            <TextField
              select
              fullWidth
              size="small"
              label="Role"
              value={filters.role}
              onChange={(e) => setFilters((prev) => ({ ...prev, role: e.target.value }))}
              SelectProps={listFiltersSelectProps(colors)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="admin">Admin</MenuItem>
              <MenuItem value="doctor">Doctor</MenuItem>
            </TextField>
          </Box>
          <Box sx={{ gridColumn: { xs: "span 12", md: "span 3" } }}>
            <TextField
              fullWidth
              size="small"
              label="Specialization"
              value={filters.specialization}
              onChange={(e) => setFilters((prev) => ({ ...prev, specialization: e.target.value }))}
            />
          </Box>
          <Box sx={{ gridColumn: { xs: "span 12", md: "span 3" } }}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Date From"
              value={filters.dateFrom}
              onChange={(e) => setFilters((prev) => ({ ...prev, dateFrom: e.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
          </Box>
          <Box sx={{ gridColumn: { xs: "span 12", md: "span 3" } }}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Date To"
              value={filters.dateTo}
              onChange={(e) => setFilters((prev) => ({ ...prev, dateTo: e.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        </Box>
      </CollapsibleListFilters>

      {/* DataGrid Card */}
      <Card
        sx={{
          background: `linear-gradient(135deg, ${colors.primary[400]} 0%, ${colors.primary[500]} 100%)`,
          borderRadius: '16px',
          border: `1px solid ${colors.grey[800]}30`,
          boxShadow: `0 4px 24px rgba(0, 0, 0, 0.1)`,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            height: 'calc(100vh - 250px)',
            minHeight: '500px',
            '& .MuiDataGrid-root': {
              border: 'none',
            },
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: `${colors.blueAccent[700]}60`,
              borderBottom: `2px solid ${colors.blueAccent[500]}40`,
              borderRadius: 0,
              fontWeight: 700,
              fontSize: '0.95rem',
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              fontWeight: 700,
            },
            '& .MuiDataGrid-cell': {
              borderBottom: `1px solid ${colors.grey[800]}20`,
              fontSize: '0.9rem',
            },
            '& .MuiDataGrid-row': {
              transition: 'all 0.3s ease',
              '&:hover': {
                backgroundColor: `${colors.blueAccent[800]}20`,
                cursor: 'pointer',
              },
            },
            '& .MuiDataGrid-footerContainer': {
              backgroundColor: `${colors.blueAccent[700]}40`,
              borderTop: `2px solid ${colors.blueAccent[500]}40`,
              borderRadius: 0,
            },
            '& .MuiTablePagination-root': {
              color: colors.grey[100],
            },
            '& .MuiDataGrid-toolbarContainer': {
              padding: 0,
            },
            '& .MuiDataGrid-virtualScroller': {
              backgroundColor: 'transparent',
            },
          }}
        >
          <DataGrid
            rows={doctors}
            columns={columns}
            loading={loading}
            autoHeight={false}
            pagination
            paginationMode="server"
            rowCount={totalCount}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[5, 10, 25, 50]}
            disableRowSelectionOnClick
            sx={{
              '& .MuiDataGrid-row:nth-of-type(even)': {
                backgroundColor: `${colors.primary[500]}20`,
              },
            }}
          />
        </Box>
      </Card>
    </Box>
  );
}
